import { useState } from 'react'
import { TabSection } from './TabSection'
import { Toggle } from '../Toggle'
import { Icon } from '../Icon'
import { deleteChannel } from '../../api/client'
import type { ChannelConfigUpdate } from '../../types'
import styles from './AdvancedTab.module.css'

interface Props {
  channelId: string
  sessionToken: string
  isAdmin: boolean
  draft: {
    paused: boolean
  }
  onChange: (patch: Partial<ChannelConfigUpdate>) => void
  onDeleted: () => void
}

export function AdvancedTab({ channelId, sessionToken, isAdmin, draft, onChange, onDeleted }: Props) {
  const [confirming, setConfirming] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  async function handleDelete() {
    setDeleting(true)
    setDeleteError(null)
    try {
      await deleteChannel(sessionToken, channelId)
      onDeleted()
    } catch (err: unknown) {
      const e = err as { status?: number; message?: string }
      if (e?.status === 403) {
        setDeleteError('Only admins can delete a channel.')
      } else {
        setDeleteError(e?.message ?? 'Something went wrong. Please try again.')
      }
      setDeleting(false)
      setConfirming(false)
    }
  }

  return (
    <TabSection
      accent="red"
      icon="settings"
      title="Advanced"
      subtitle="Pause all activity for this channel"
    >
      <div className={styles.dangerZone}>
        <Toggle
          label="Pause all notifications"
          hint="Temporarily silence all bot activity — PR updates, nudges, and digest will not be sent until re-enabled"
          checked={draft.paused}
          onChange={v => onChange({ paused: v })}
          danger
          noBorder={isAdmin}
        />

        {isAdmin && (
          <div className={styles.deleteSection}>
            {!confirming ? (
              <div className={styles.deleteRow}>
                <div className={styles.deleteInfo}>
                  <div className={styles.deleteLabel}>Remove channel</div>
                  <div className={styles.deleteHint}>
                    Permanently removes all config, subscriptions and PR trackers. Irreversible.
                  </div>
                </div>
                <button className={styles.deleteBtn} onClick={() => setConfirming(true)}>
                  <Icon name="delete_forever" size="sm" /> Remove
                </button>
              </div>
            ) : (
              <div className={styles.confirmBox}>
                <div className={styles.confirmText}>
                  <Icon name="warning_amber" size="sm" style={{ color: 'var(--red)', flexShrink: 0 }} />
                  This will permanently remove the channel and all its data. Are you sure?
                </div>
                <div className={styles.confirmActions}>
                  <button className={styles.cancelBtn} onClick={() => setConfirming(false)} disabled={deleting}>
                    Cancel
                  </button>
                  <button className={styles.confirmDeleteBtn} onClick={handleDelete} disabled={deleting}>
                    {deleting
                      ? <><Icon name="hourglass_empty" size="sm" /> Removing…</>
                      : <><Icon name="delete_forever" size="sm" /> Yes, remove channel</>
                    }
                  </button>
                </div>
                {deleteError && (
                  <div className={styles.deleteError}>
                    <Icon name="error_outline" size="sm" /> {deleteError}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </TabSection>
  )
}
